import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetWorkshopInterestQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';
import {resolvePairedRecordCode} from './resolvePairedRecordCode';

export function useWorkshopInterestPairedRecord(
  workshopInterestId: string | null
): PairedRecordView {
  const {data: workshopInterest, isLoading} = useGetWorkshopInterestQuery(
    isNotNil(workshopInterestId) ? {id: workshopInterestId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const createdAtFormatted = workshopInterest?.createdAt
    ? formatDateTime('dateShort', parseDate(workshopInterest.createdAt))
    : null;
  const updatedAtFormatted = workshopInterest?.updatedAt
    ? formatDateTime('dateShort', parseDate(workshopInterest.updatedAt))
    : null;
  const firstContactAtFormatted = workshopInterest?.firstContactAt
    ? formatDateTime('dateShort', parseDate(workshopInterest.firstContactAt))
    : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(
      isNotNil(updatedAtFormatted),
      `${i18n.t('general.labels.lastUpdated')} ${updatedAtFormatted}`
    )
    .when(
      isNotNil(firstContactAtFormatted),
      `${i18n.t('page.recordAudit.pairing.firstContact')} ${firstContactAtFormatted}`
    );

  return {
    isLoading,
    code: resolvePairedRecordCode(workshopInterest?.publicId, isLoading),
    detailPath: isNotNil(workshopInterestId)
      ? composePath(workshopRoutes.interestDetailOverview, {params: {id: workshopInterestId}})
      : null,
    parameters,
    vehicleIds: isNotNil(workshopInterest?.vehicleId) ? [workshopInterest.vehicleId] : [],
  };
}
